import { useEffect, useState } from 'react';
import { Bot, Download, Upload, Image as ImageIcon, PencilLine, ShieldCheck, X } from 'lucide-react';
import { useAppStore } from '../stores/appStore';

/** Bump the key to announce something new — shown once per browser *per tenant*. */
const STORAGE_KEY = 'ibot.whatsNew.2026-10-import-assinado';

function hasSeen(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return true; // storage blocked → never nag
  }
}

function markSeen(key: string): void {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // ignore
  }
}

/** `?whatsnew=1` in the URL forces the announcement (handy for demos). */
function isForced(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('whatsnew') === '1';
  } catch {
    return false;
  }
}

const providers = [
  { src: '/providers/openai.svg', alt: 'OpenAI' },
  { src: '/providers/gemini.svg', alt: 'Google Gemini' },
  { src: '/providers/claude.svg', alt: 'Claude' },
];

const highlights = [
  {
    icon: PencilLine,
    title: 'Qualquer modelo, na hora',
    text: 'Escolha "Outro modelo" e digite o ID de um lançamento novo, sem esperar atualização.',
  },
  {
    icon: ImageIcon,
    title: 'Claude agora lê imagens',
    text: 'O nó Analisar Imagem ganhou o Claude, ao lado da OpenAI e do Gemini.',
  },
  {
    icon: Download,
    title: 'Exportar fluxo',
    text: 'Baixe o fluxo inteiro em um arquivo — passos, ligações e configurações.',
  },
  {
    icon: Upload,
    title: 'Importar fluxo',
    text: 'Suba o arquivo, confira o resumo e confirme. Entra como rascunho novo, sem mexer no que já existe.',
  },
];

/**
 * One-time "what's new" announcement. Only the close button (✕ or the primary
 * action) marks it as read — dismissing with Esc or a click outside leaves it
 * pending, so it comes back on the next visit.
 */
export function WhatsNewModal() {
  const tenant = useAppStore((state) => state.tenant);
  const storageKey = `${STORAGE_KEY}:${tenant?.id ?? 'anon'}`;
  const [open, setOpen] = useState(false);

  const dismissForGood = () => {
    markSeen(storageKey);
    setOpen(false);
  };

  const hideForNow = () => setOpen(false);

  useEffect(() => {
    if (!isForced() && hasSeen(storageKey)) return;
    const timer = window.setTimeout(() => setOpen(true), 900); // let the app settle first
    return () => window.clearTimeout(timer);
  }, [storageKey]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hideForNow();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-title"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[3px]" onClick={hideForNow} />

      {/* `flowbuild dark` scopes the builder palette to the card only (display: contents = no box) */}
      <div className="flowbuild dark contents">
        <div className="relative max-h-[calc(100vh-32px)] w-[min(600px,100%)] overflow-y-auto overflow-x-hidden rounded-2xl border border-slate-700/70 bg-slate-900 text-slate-100 shadow-[0_30px_90px_rgba(0,0,0,0.7)]">
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 left-1/2 h-48 w-[420px] -translate-x-1/2 rounded-full bg-blue-600/25 blur-3xl"
          />

          <div className="relative flex items-start gap-3 px-6 pt-6">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-blue-500/20 text-blue-300">
              <Bot className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-blue-300">Novidades</p>
              <h2 id="whats-new-title" className="mt-0.5 text-[20px] font-semibold leading-tight">
                Modelos de IA novos e fluxos que viajam
              </h2>
            </div>
            <button
              type="button"
              onClick={dismissForGood}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200"
              title="Fechar e não mostrar novamente"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* headline: the new AI models */}
          <div className="relative mx-6 mt-4 rounded-xl border border-slate-700/70 bg-slate-800/50 p-4">
            <div className="flex items-center gap-2">
              {providers.map((provider) => (
                <img
                  key={provider.alt}
                  src={provider.src}
                  alt={provider.alt}
                  className="h-7 w-auto rounded-md"
                />
              ))}
            </div>
            <p className="mt-3 text-[13.5px] font-medium leading-snug">
              Os modelos mais recentes dos três provedores já estão na lista
            </p>
            <p className="mt-1 text-[12.5px] leading-relaxed text-slate-400">
              <span className="text-slate-200">GPT-6</span> (Luna, Sol e Astra),{' '}
              <span className="text-slate-200">Gemini 3</span> e{' '}
              <span className="text-slate-200">Claude Opus 5.5 e Fable 5.1</span>. O GPT-6 Luna virou o padrão
              dos nós novos — é quatro vezes mais barato que o modelo que usávamos antes.
            </p>
          </div>

          <ul className="relative mt-3 grid gap-2 px-6 sm:grid-cols-2">
            {highlights.map(({ icon: Icon, title, text }) => (
              <li key={title} className="flex gap-2.5 rounded-xl border border-slate-700/60 bg-slate-800/50 p-3">
                <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md bg-slate-800 text-blue-300">
                  <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-[13px] font-medium leading-snug">{title}</p>
                  <p className="mt-0.5 text-[12px] leading-snug text-slate-400">{text}</p>
                </div>
              </li>
            ))}
          </ul>

          {/* the import is new in this release — say plainly why it refuses files */}
          <div className="relative mx-6 mt-3 flex gap-2.5 rounded-xl border border-blue-500/30 bg-blue-500/10 p-3">
            <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md bg-blue-500/20 text-blue-300">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="text-[13px] font-medium leading-snug">A importação só aceita arquivo do iBot</p>
              <p className="mt-0.5 text-[12px] leading-relaxed text-slate-300/90">
                Cada fluxo exportado sai com uma assinatura. Se o arquivo for editado por fora — mesmo um
                caractere — a importação recusa e avisa o motivo. Fluxo vindo de outra plataforma não entra
                por aqui: fale com o suporte que a gente converte.
              </p>
            </div>
          </div>

          <div className="relative flex items-center justify-end px-6 py-5">
            <button
              type="button"
              onClick={dismissForGood}
              className="h-10 rounded-lg bg-blue-600 px-5 text-[13.5px] font-medium text-white transition-colors hover:bg-blue-500"
            >
              Entendi, não mostrar de novo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
